/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '@statsbomb/kitbag-themes';
import { useReactiveVar } from '@apollo/client';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import { getAppTheme } from '../../utils/helpers/appTheme';
import { ProtectedRoute } from '../../apollo/ProtectedRoute';
import {
  ui_isDark,
  currentLocationRV,
  mf_Teams,
  mf_Leagues,
  mf_LeagueLevel,
} from '../../apollo';
import { GroupOnlyRoute } from '../../apollo/GroupOnlyRoute';
import Layout from '../Layout/Layout';
import ReleaseNotes from '../../pages/ReleaseNotes/ReleaseNotes';

import Player from '../../pages/player/Player';
import PlayerOverview from '../../pages/player/PlayerOverview/PlayerOverview';
import PlayerPassPlacement from '../../pages/player/PlayerPassPlacement/PlayerPassPlacement';
import PassEnds from '../../pages/player/PlayerPassing/PassEnds';
import PlayerRadar from '../../pages/player/PlayerRadar/PlayerRadar';
import PlayerSnap from '../../pages/player/PlayerSnap/PlayerSnap';

import BoxScore from '../../pages/team/TeamBoxScore/TeamBoxScore';
import PassPressures from '../../pages/team/lineBattles/PassPressures/PassPressures';
import TeamPassing from '../../pages/team/TeamPassing/TeamPassing';
import TeamDepthChart from '../../pages/team/TeamDepthChart/TeamDepthChart';
import TeamFormationSummary from '../../pages/team/TeamFormationSummary/TeamFormationSummary';

import PlayerStats from '../../pages/League/PlayerStats/PlayerStats';
import TeamStats from '../../pages/League/TeamStats/TeamStats';

import Game from '../../pages/Game/Game';
import GameAnimation from '../../pages/Game/GameAnimation/GameAnimation';
import GameSnapFormations from '../../pages/Game/GameSnapFormations/GameSnapFormations';
import GamePlayEvents from '../../pages/Game/GamePlayEvents/GamePlayEvents';
import GameOverview from '../../pages/Game/GameOverview/GameOverview';
import DataAccess from '../../pages/DataAccess/DataAccess';

import SafePaletteViewer from '../../pages/Dev/SafePaletteViewer';

import '../../styles/index.scss';
import { ThemeCustomProperties } from '../../styles/themeCustomProperties';
import { USER_GROUP } from '../../utils/constants/auth';
import TeamTackleAttempts from '../../pages/team/TeamTackling/TeamTackleAttempts';
import TeamPlusMinus from '../../pages/team/TeamPlusMinus/TeamPlusMinus';
import RunTendencies from '../../pages/team/lineBattles/RunTendencies/RunTendencies';
import TeamOverview from '../../pages/team/TeamOverview/TeamOverview';
import TeamRadar from '../../pages/team/TeamRadar/TeamRadar';
import TeamFormationDetail from '../../pages/team/TeamFormationDetail/TeamFormationDetail';
import PlayerRoute from '../../pages/player/PlayerRoute/PlayerRoute';
import TeamSchedule from '../../pages/team/TeamSchedule/TeamSchedule';
import ScoutResults from '../../pages/Scout/ScoutResults/ScoutResults';
import { PRODUCTION, PAGE } from './index.constants';
import PlayerComparison from '../../pages/player/PlayerComparison/PlayerComparison';
import Admin from '../../pages/Admin/Admin';
import BasicPage from '../Layout/BasicPage';
import PlayFinder from '../../pages/PlayFinder/PlayFinder';
import PlayFinderWizard from '../../pages/PlayFinder/PlayFinderWizard/PlayFinderWizard';
import PlayFinderResults from '../../pages/PlayFinder/PlayFinderResults/PlayFinderResults';
import HavocSummary from '../../pages/team/TeamHavoc/HavocSummary/HavocSummary';
import TeamDefensiveScheme from '../../pages/team/TeamDefensiveScheme/TeamDefensiveScheme';
import { pitchCompetitionLevel } from '../../utils/helpers/field.constants';
import PlayByPlayPageWrapper from '../../pages/Game/GamePlayByPlay/PlayByPlayPageWrapper';
import { initialiseSnowplow } from '../../utils/helpers/snowplow';
import Playlists from '../../pages/Playlists/Playlists';
import { useUserConfigContext } from '../../utils/providers/UserConfigProvider';
import { useAccountConfigContext } from '../../utils/providers/AccountConfigProvider';
import { useStore } from '../../store/useStore';

const App = function () {
  const { account: teamAccount } = useAccountConfigContext();
  const { account: userAccount } = useUserConfigContext();
  const setSavedScoutTemplates = useStore((s) => s.setSavedScoutTemplates);
  const setScoutFavourites = useStore((s) => s.setScoutFavourites);
  const setScoutToken = useStore((s) => s.setToken);
  const ui_isDarkRV = useReactiveVar(ui_isDark);
  const location = useLocation();
  const firstLoad = useRef(false);
  const logRocketInitialized = useRef(false);
  const { DEFAULT, WITH_CONTEXT_FILTERS } = PAGE;

  const { user, getAccessTokenSilently } = useKitbagAuth();
  useEffect(() => {
    if (user && getAccessTokenSilently) {
      initialiseSnowplow(user.sub, user['https://statsbomb.com/accountID']);
      (async () => {
        try {
          const token = await getAccessTokenSilently();
          setScoutToken(token);
        } catch (e) {
          setScoutToken(null);
        }
      })();
    }
  }, [user, getAccessTokenSilently, setScoutToken]);

  useEffect(() => {
    currentLocationRV(location.pathname);
  }, [location]);

  useEffect(() => {
    // an empty config ({}) or a null isDark must not reset the current theme
    if (typeof userAccount?.isDark === 'boolean') {
      ui_isDark(userAccount.isDark);
    }
  }, [userAccount]);

  useEffect(() => {
    if (!firstLoad.current && teamAccount) {
      setSavedScoutTemplates(teamAccount?.scoutSetups);
      setScoutFavourites(teamAccount?.scoutFavourites);
      firstLoad.current = true;
    }
    // invoke account saved defaults if the URL is http://{TLD}/
    // else they are overridden (in <Layout>) by the URL supplied params
    if (
      location.pathname.length <= 1 &&
      teamAccount?.defaultCompetition &&
      teamAccount?.defaultTeam
    ) {
      mf_Leagues(teamAccount.defaultCompetition);
      mf_LeagueLevel(pitchCompetitionLevel(teamAccount.defaultCompetition));
      mf_Teams(teamAccount.defaultTeam);
    }
  }, [teamAccount]);

  const theme = getAppTheme(ui_isDarkRV);

  useEffect(() => {
    if (
      process.env.NODE_ENV !== PRODUCTION ||
      !user ||
      logRocketInitialized.current
    )
      return;

    // Check if user's org is in the exclusion list
    const userOrgId = user['https://statsbomb.com/accountID'];
    const excludedOrgs = process.env.LOGROCKET_EXCLUDED_ORGS
      ? process.env.LOGROCKET_EXCLUDED_ORGS.split(',').map((id) => id.trim())
      : [];

    const isExcludedOrg = excludedOrgs.includes(userOrgId);

    // Initialize Sentry for all users
    Sentry.init({
      dsn: process.env.SENTRY_DSN,
      integrations: [
        new Sentry.BrowserTracing({
          tracePropagationTargets: [/^https:\/\/iq-amf.statsbomb\.com/],
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: Number(process.env.SENTRY_TRACES_SAMPLE_RATE), // 1.0 = capture 100% of the transactions

      beforeSend: async (event) => {
        // Only attempt to get LogRocket session URL if LogRocket is initialized
        if (!isExcludedOrg) {
          let { sessionURL } = LogRocket;

          // If it doesn't exist yet, ask LogRocket for it
          if (!sessionURL) {
            sessionURL = await new Promise((resolve) => {
              LogRocket.getSessionURL((url) => {
                resolve(url);
              });
            });
          }

          // If we ended up with a URL, merge it into the event's `extra` payload.
          if (sessionURL) {
            return {
              ...event,
              extra: {
                ...(event.extra || {}),
                sessionURL,
              },
            };
          }
        }

        // Fallback: no session URL available, just send the event unchanged.
        return event;
      },
    });

    // Add user details to Sentry (for all users)
    const sentryUserData = {
      id: user.sub,
      email: user.email,
      username: user.name,
      organization: userOrgId,
    };
    Sentry.setUser(sentryUserData);

    // Only initialize LogRocket if user is NOT in an excluded org
    if (!isExcludedOrg) {
      LogRocket.init('erd/hudliq');

      // Add logRocket user details
      LogRocket.identify(user.sub, {
        name: user.name,
        email: user.email,
        orgId: userOrgId,
      });

      // Ask LogRocket for the current session replay URL,
      // then stash it in Sentry's scope as extra data
      LogRocket.getSessionURL((url) => {
        Sentry.configureScope((scope) => {
          scope.setExtra('sessionURL', url);
        });
      });
    } else {
      // eslint-disable-next-line no-console
      console.info(
        `LogRocket is not enabled for this session (org: ${userOrgId}). Sentry error tracking is still active.`,
      );
    }

    logRocketInitialized.current = true;
  }, [user]);

  return (
    <ThemeProvider theme={theme}>
      <ThemeCustomProperties />
      <Routes>
        <Route element={<ProtectedRoute component={Layout} />}>
          <Route element={<BasicPage top={WITH_CONTEXT_FILTERS.top} />}>
            <Route path="" element={<TeamOverview />} />
            <Route path="data-access" element={<DataAccess />} />
          </Route>
          <Route
            path="/:leagues/:seasons/:teams"
            element={<BasicPage top={WITH_CONTEXT_FILTERS.top} />}
          >
            <Route path="" element={<TeamOverview />} />
          </Route>
          <Route
            path="player"
            element={<Player top={WITH_CONTEXT_FILTERS.top} />}
          >
            <Route
              path="overview/:leagues/:seasons/:teams/:players"
              element={<PlayerOverview />}
            />
            <Route
              path="placement/:leagues/:seasons/:teams/:players"
              element={<PlayerPassPlacement />}
            />
            <Route
              path="snap/:leagues/:seasons/:teams/:players"
              element={<PlayerSnap />}
            />
            <Route
              path="radar/:leagues/:seasons/:teams/:players"
              element={<PlayerRadar />}
            />
            <Route
              path="route/:leagues/:seasons/:teams/:players"
              element={<PlayerRoute />}
            />
            <Route
              path="passing/:leagues/:seasons/:teams/:players"
              element={<PassEnds />}
            />
          </Route>
          <Route path="player" element={<Player top={DEFAULT.top} />}>
            <Route
              path="comparison/:leagues/:seasons/:teams/:players"
              element={<PlayerComparison />}
            />
          </Route>
          <Route
            path="team"
            element={<BasicPage top={WITH_CONTEXT_FILTERS.top} />}
          >
            <Route path="box/:leagues/:seasons/:teams" element={<BoxScore />} />
            <Route
              path="defensiveScheme/:leagues/:seasons/:teams"
              element={<TeamDefensiveScheme />}
            />
            <Route
              path="linePressure/:leagues/:seasons/:teams"
              element={<PassPressures />}
            />
            <Route
              path="runTendencies/:leagues/:seasons/:teams"
              element={<RunTendencies />}
            />
          </Route>
          <Route path="team" element={<BasicPage top={DEFAULT.top} />}>
            <Route
              path="schedule/:leagues/:seasons/:teams"
              element={<TeamSchedule />}
            />
          </Route>
          <Route
            path="team"
            element={<BasicPage top={WITH_CONTEXT_FILTERS.top} />}
          >
            <Route
              path="overview/:leagues/:seasons/:teams"
              element={<TeamOverview />}
            />
            <Route
              path="passing/:leagues/:seasons/:teams"
              element={<TeamPassing />}
            />
            <Route
              path="havoc-summary/:leagues/:seasons/:teams"
              element={<HavocSummary />}
            />
            <Route
              path="tackles/:leagues/:seasons/:teams"
              element={<TeamTackleAttempts />}
            />
            <Route
              path="depth/:leagues/:seasons/:teams"
              element={<TeamDepthChart />}
            />
            <Route
              path="summary/:leagues/:seasons/:teams"
              element={<TeamFormationSummary />}
            />
            <Route
              path="detail/:leagues/:seasons/:teams"
              element={
                <GroupOnlyRoute group={USER_GROUP.STATSBOMB}>
                  <TeamFormationDetail />
                </GroupOnlyRoute>
              }
            />
            <Route
              path="impact/:leagues/:seasons/:teams"
              element={
                <GroupOnlyRoute group={USER_GROUP.STATSBOMB}>
                  <TeamPlusMinus />
                </GroupOnlyRoute>
              }
            />
            <Route
              path="radar/:leagues/:seasons/:teams"
              element={<TeamRadar />}
            />
          </Route>
          <Route
            path="team"
            element={<BasicPage top={WITH_CONTEXT_FILTERS.top} />}
          >
            <Route
              path="overview/:leagues/:seasons/:teams"
              element={<TeamOverview />}
            />
            <Route
              path="passing/:leagues/:seasons/:teams"
              element={<TeamPassing />}
            />
            <Route
              path="havocSummary/:leagues/:seasons/:teams"
              element={<HavocSummary />}
            />
          </Route>
          <Route path="playfinder" element={<PlayFinder top={DEFAULT.top} />}>
            <Route path="wizard/" element={<PlayFinderWizard />} />
            <Route
              path="results/:leagues/:seasons/:teams/:offense"
              element={<PlayFinderResults />}
            />
            <Route
              path="results/:leagues/:seasons/:teams/:offense/:preset"
              element={<PlayFinderResults />}
            />
          </Route>
          <Route path="playlists" element={<Playlists top={DEFAULT.top} />} />
          <Route
            path="league"
            element={<BasicPage top={WITH_CONTEXT_FILTERS.top} />}
          >
            <Route path="stats">
              <Route
                path="players/:leagues/:seasons"
                element={<PlayerStats />}
              />
              <Route path="teams/:leagues/:seasons" element={<TeamStats />} />
            </Route>
          </Route>
          <Route path="game" element={<Game />}>
            <Route
              path="animation/:leagues/:seasons/:teams/:games/:drives/:plays"
              element={<GameAnimation />}
            />
            <Route
              path="formations/:leagues/:seasons/:teams/:games/:drives"
              element={<GameSnapFormations />}
            />
            <Route
              path="event/:leagues/:seasons/:teams/:games/:drives/:plays"
              element={<GamePlayEvents />}
            />
          </Route>
          <Route path="game" element={<BasicPage top={DEFAULT.top} />}>
            <Route
              path="overview/:leagues/:seasons/:teams/:games"
              element={<GameOverview />}
            />
            <Route
              path="byplay/:leagues/:seasons/:teams/:games"
              element={<PlayByPlayPageWrapper />}
            />
          </Route>
          <Route path="scout" element={<BasicPage top={DEFAULT.top} />}>
            <Route
              path="players/:leagues/:seasons"
              element={<ScoutResults />}
            />
          </Route>
          <Route path="help">
            <Route path="release-notes" element={<ReleaseNotes />} />
          </Route>
          {/* This page is internal use only so should never appear in any menu */}
          <Route path="safe" element={<BasicPage top={DEFAULT.top} />}>
            <Route
              path="palette/:leagues/:seasons"
              element={
                <GroupOnlyRoute group={USER_GROUP.STATSBOMB}>
                  <SafePaletteViewer />
                </GroupOnlyRoute>
              }
            />
          </Route>
          <Route path="/admin" element={<BasicPage top={DEFAULT.top} />}>
            <Route path="" element={<Admin />} />
          </Route>
        </Route>
      </Routes>
    </ThemeProvider>
  );
};

export default App;
